import React, { useState, useEffect, useCallback } from 'react';
import { Button, Badge, Table, Alert, Dropdown, Spinner } from 'react-bootstrap';
import {
    Mail,
    Plus,
    Pause,
    Play,
    Trash2,
    ChevronDown,
    ChevronUp,
    AlertTriangle,
    RefreshCw,
} from 'lucide-react';
import ProviderIcon from '../Common/ProviderIcon';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { syncConnection } from '../../../services/InboxService';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { useLocation, useHistory } from 'react-router-dom';

/**
 * EmailInboxSettings — manage Gmail/Outlook OAuth connections for the
 * unified Inbox.
 *
 * Two important things to know about this page:
 *
 *   1. OAuth callback round-trip: the user clicks "Anslut e-post", we
 *      POST /authorize, backend signs a state JWT and returns a Google
 *      or Microsoft consent URL. The browser navigates there, the user
 *      grants consent, the provider redirects back to our BACKEND's
 *      /email-inbox/oauth/callback, which (after exchanging the code)
 *      302s back to THIS page with `?email_connected=success|error&reason=…`.
 *      We read those params on mount, show a toast, then strip them from
 *      the URL so the user can leave/return without re-firing the toast.
 *
 *   2. No "Skanna" / one-shot scan button. The old prototype let users
 *      manually trigger an ingest from this page; that's been removed
 *      because the backend now drives polling via the Azure Function
 *      poller on its own schedule. The Settings page is for connection
 *      *management* only — actual incoming items show up in the Inkorg
 *      sidebar page.
 */

const STATUS_BADGES = {
    active: { variant: 'success', label: 'Aktiv' },
    paused: { variant: 'warning', label: 'Pausad' },
    error: { variant: 'danger', label: 'Fel' },
    disconnected: { variant: 'secondary', label: 'Frånkopplad' },
};

function formatDate(dateStr) {
    if (!dateStr) return '—';
    try {
        return format(new Date(dateStr), 'yyyy-MM-dd HH:mm', { locale: sv });
    } catch {
        return dateStr;
    }
}

const EmailInboxSettings = ({ companyId }) => {
    const [connections, setConnections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [expandedHistory, setExpandedHistory] = useState(null);
    const [historyData, setHistoryData] = useState({});

    const location = useLocation();
    const history = useHistory();

    const fetchConnections = useCallback(async () => {
        try {
            setIsLoading(true);
            const url = getApiUrl(`${companyId}/email-inbox/connections`);
            const { data } = await axios.get(url, { withCredentials: true });
            setConnections(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching email connections:', err);
            toast.error('Kunde inte hämta e-postanslutningar');
        } finally {
            setIsLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        fetchConnections();
    }, [fetchConnections]);

    // OAuth completion handler. The backend redirects here with one of:
    //   ?email_connected=success
    //   ?email_connected=error&reason=<bounded enum>
    // We show a toast, then clean the URL so the toast doesn't re-fire on refresh.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const status = params.get('email_connected');
        if (!status) return;
        const reason = params.get('reason');

        if (status === 'success') {
            toast.success('E-postanslutning klar — vi börjar hämta fakturor inom kort.');
            fetchConnections();
        } else {
            const friendly = {
                invalid_state: 'Ogiltig OAuth-begäran (försök igen).',
                invalid_grant: 'Inloggningen avbröts eller medgivande togs tillbaka.',
                exchange_failed: 'Kunde inte byta inloggningskod mot åtkomst.',
                missing_code: 'Inloggningen blev ofullständig.',
                company_inactive: 'Företaget är inte aktivt.',
            }[reason] || (reason ? `Anslutningen misslyckades (${reason}).` : 'Anslutningen misslyckades.');
            toast.error(friendly);
        }

        // Strip the query params without triggering a re-render of the route.
        params.delete('email_connected');
        params.delete('reason');
        const remaining = params.toString();
        history.replace({ pathname: location.pathname, search: remaining ? `?${remaining}` : '', hash: location.hash });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const handleConnect = async (provider) => {
        try {
            setActionLoading('connect');
            const url = getApiUrl(`${companyId}/email-inbox/connections/authorize`);
            const { data } = await axios.post(url, { provider }, { withCredentials: true });
            if (data?.authorizationUrl) {
                // Full-page redirect to the provider's consent screen. We
                // intentionally don't use a popup because (a) iframes are
                // blocked by Google/Microsoft, (b) the round-trip back to
                // /settings is what we want anyway.
                window.location.href = data.authorizationUrl;
            }
        } catch (err) {
            console.error('Error starting OAuth:', err);
            toast.error('Kunde inte starta anslutningen');
        } finally {
            setActionLoading(null);
        }
    };

    const updateConnection = async (connectionId, action, body = {}, successMsg) => {
        try {
            setActionLoading(connectionId);
            const url = getApiUrl(`${companyId}/email-inbox/connections/${connectionId}${action ? `/${action}` : ''}`);
            const verb = action ? 'post' : 'patch';
            await axios[verb](url, body, { withCredentials: true });
            toast.success(successMsg);
            fetchConnections();
        } catch (err) {
            console.error(`Error on ${action}:`, err);
            toast.error('Något gick fel');
        } finally {
            setActionLoading(null);
        }
    };

    const handlePause = (id) => updateConnection(id, 'pause', {}, 'Anslutning pausad');
    const handleResume = (id) => updateConnection(id, 'resume', {}, 'Anslutning återupptagen');

    // Per-connection on-demand sync. Backend fetches from this mailbox
    // now and feeds anything new into the Inkorg. Independent of the
    // Function's scheduled polling.
    const handleSync = async (connectionId) => {
        try {
            setActionLoading(connectionId);
            const result = await syncConnection(companyId, connectionId);
            if (result.created > 0) {
                toast.success(`${result.created} nya fakturor hämtade.`);
            } else if (result.fetched === 0) {
                toast.info('Inga nya meddelanden i denna inkorg.');
            } else {
                toast.info(`Hämtning klar — inga nya fakturor (${result.skipped} hoppade).`);
            }
            fetchConnections();
        } catch (err) {
            const msg = err?.response?.data?.error || err.message || 'Synkningen misslyckades';
            toast.error(msg);
        } finally {
            setActionLoading(null);
        }
    };

    const handleDisconnect = async (connectionId) => {
        try {
            setActionLoading(connectionId);
            const url = getApiUrl(`${companyId}/email-inbox/connections/${connectionId}`);
            await axios.delete(url, { withCredentials: true });
            toast.success('Anslutning borttagen');
            fetchConnections();
        } catch (err) {
            console.error('Error disconnecting:', err);
            toast.error('Kunde inte ta bort anslutningen');
        } finally {
            setActionLoading(null);
        }
    };

    const toggleHistory = async (connectionId) => {
        if (expandedHistory === connectionId) {
            setExpandedHistory(null);
            return;
        }
        setExpandedHistory(connectionId);
        if (!historyData[connectionId]) {
            try {
                const url = getApiUrl(`${companyId}/email-inbox/connections/${connectionId}/history?limit=20`);
                const { data } = await axios.get(url, { withCredentials: true });
                setHistoryData((prev) => ({ ...prev, [connectionId]: data }));
            } catch (err) {
                console.error('Error fetching history:', err);
                toast.error('Kunde inte hämta historik');
            }
        }
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <div className="modern-card">
            <div className="modern-card-header">
                <div>
                    <h2 className="section-title">
                        <Mail size={24} /> E-postinkorg
                    </h2>
                    <p className="section-subtitle">
                        Anslut Gmail eller Outlook så hämtas fakturor automatiskt från din inkorg till{' '}
                        <a href={`/${companyId}/inbox`}>Inkorgen</a>.
                    </p>
                </div>
                <div className="action-buttons">
                    <Dropdown>
                        <Dropdown.Toggle
                            variant="primary"
                            size="sm"
                            disabled={actionLoading === 'connect'}
                        >
                            <Plus size={14} /> Anslut e-post
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleConnect('gmail')}>Gmail</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleConnect('outlook')}>Outlook</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            <div className="modern-card-body">
            {connections.length === 0 ? (
                <Alert variant="info" style={{ fontSize: 13 }}>
                    <Mail size={14} className="me-2" />
                    Ingen e-postinkorg är ansluten. Klicka på "Anslut e-post" för att komma igång.
                </Alert>
            ) : (
                connections.map((conn) => {
                    const statusBadge = STATUS_BADGES[conn.status] || STATUS_BADGES.disconnected;
                    const isExpanded = expandedHistory === conn.connection_id;
                    const acting = actionLoading === conn.connection_id;
                    return (
                        <div
                            key={conn.connection_id}
                            style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: 8,
                                padding: 16,
                                marginBottom: 12,
                            }}
                        >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                            <ProviderIcon provider={conn.provider} />
                                            <h5 style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{conn.email_address}</h5>
                                            {/* "Aktiv" is the normal state — only badge the exceptions. */}
                                            {conn.status !== 'active' && (
                                                <Badge bg={statusBadge.variant}>{statusBadge.label}</Badge>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: 12, color: '#6b7280', fontSize: 12, flexWrap: 'wrap' }}>
                                            {conn.last_poll_at && <span>Senast hämtad: {formatDate(conn.last_poll_at)}</span>}
                                        </div>
                                        {conn.status === 'error' && conn.last_error && (
                                            <Alert variant="danger" className="mt-2 mb-0 py-1 px-2" style={{ fontSize: 12 }}>
                                                <AlertTriangle size={12} className="me-1" />
                                                {conn.last_error_code ? <strong>{conn.last_error_code}:</strong> : null} {conn.last_error}
                                            </Alert>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        {(conn.status === 'active' || conn.status === 'paused') && (
                                            <Button variant="outline-primary" size="sm" onClick={() => handleSync(conn.connection_id)} disabled={acting} title="Hämta nya meddelanden nu">
                                                {acting ? <Spinner animation="border" size="sm" /> : <RefreshCw size={14} />}
                                            </Button>
                                        )}
                                        {conn.status === 'active' && (
                                            <Button variant="outline-warning" size="sm" onClick={() => handlePause(conn.connection_id)} disabled={acting} title="Pausa">
                                                <Pause size={14} />
                                            </Button>
                                        )}
                                        {(conn.status === 'paused' || conn.status === 'error') && (
                                            <Button variant="outline-success" size="sm" onClick={() => handleResume(conn.connection_id)} disabled={acting} title="Återuppta">
                                                <Play size={14} />
                                            </Button>
                                        )}
                                        <Button variant="outline-danger" size="sm" onClick={() => handleDisconnect(conn.connection_id)} disabled={acting} title="Koppla från">
                                            <Trash2 size={14} />
                                        </Button>
                                        <Button variant="outline-secondary" size="sm" onClick={() => toggleHistory(conn.connection_id)} title="Historik">
                                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                        </Button>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="mt-3">
                                        <h6 style={{ fontSize: 13 }}>Bearbetade meddelanden</h6>
                                        {!historyData[conn.connection_id] ? (
                                            <div className="text-center py-2">
                                                <Spinner animation="border" size="sm" />
                                            </div>
                                        ) : historyData[conn.connection_id].length === 0 ? (
                                            <p style={{ color: '#9ca3af', fontSize: 13 }}>Inga meddelanden har bearbetats ännu.</p>
                                        ) : (
                                            <Table size="sm" hover responsive style={{ fontSize: 13 }}>
                                                <thead>
                                                    <tr>
                                                        <th>Datum</th>
                                                        <th>Ämne</th>
                                                        <th>Avsändare</th>
                                                        <th>Bilagor</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historyData[conn.connection_id].map((msg) => (
                                                        <tr key={msg.id}>
                                                            <td className="text-nowrap">{formatDate(msg.processed_at)}</td>
                                                            <td>{msg.subject || <span style={{ color: '#9ca3af' }}>(inget ämne)</span>}</td>
                                                            <td>{msg.sender_email || '—'}</td>
                                                            <td>{msg.attachment_count}</td>
                                                            <td>
                                                                <Badge bg={msg.status === 'processed' ? 'success' : msg.status === 'skipped' ? 'secondary' : 'danger'}>
                                                                    {msg.status === 'processed' ? 'Bearbetad' : msg.status === 'skipped' ? 'Hoppad' : 'Fel'}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        )}
                                    </div>
                                )}
                        </div>
                    );
                })
            )}
            </div>
        </div>
    );
};

export default EmailInboxSettings;
